// Figma: https://www.figma.com/design/nykbXADUTliE6d7xv6FjcQ/NEW-TSAB-Shared-Customer-Library?node-id=52-1113
// Figma component name: PriceToggle
// Replaces: TwoOptionField + TwoOptionSwitch for the fixedPrice field in SelectHow

import classnames from 'classnames'

// Radiobutton indicator — matches Figma Radiobutton component (node 52:1061 / 52:1063)
function RadioIcon({ selected }) {
  if (selected) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
        <circle cx="12" cy="12" r="11" fill="#FFCC00" />
        <path d="M7.5 12l3 3 5.5-6" stroke="#17181a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="shrink-0">
      <circle cx="12" cy="12" r="10.5" stroke="#8c8c8c" strokeWidth="1" />
    </svg>
  )
}

/**
 * FixedPriceSwitch — Formik field component for the fixed price / taxameter toggle.
 *
 * Props (Formik field component interface):
 *   field.value  boolean — true = Fixed price selected, false = Taxameter selected
 *   form         Formik form helpers
 *   disabled     boolean — locks both options, shows current selection as forced
 *   children     rendered below the toggle (error/info messages from SelectHow)
 */
export default function FixedPriceSwitch({ field, form, disabled, children }) {
  const isFixed = Boolean(field.value)

  const handleSelect = (selectFixed) => {
    if (disabled) return
    form.setFieldValue(field.name, selectFixed)
    form.setFieldTouched(field.name, true, false)
  }

  return (
    <div className="flex flex-col gap-1 w-full">
      <div
        className={classnames(
          'relative flex h-9 w-full items-center rounded-button border-solid border-[0.5px] border-outline overflow-hidden',
          disabled ? 'bg-on-surface-variant/[0.08]' : 'bg-[#f2f2f2]',
        )}
      >
        {/* Sliding background indicator */}
        <div
          aria-hidden="true"
          className={classnames(
            'absolute top-0 left-0 h-full w-1/2 rounded-button bg-primary',
            'transition-transform duration-200 ease-in-out',
            !isFixed && 'translate-x-full',
          )}
        />

        {/* Fixed price option */}
        <button
          type="button"
          onClick={() => handleSelect(true)}
          disabled={disabled}
          aria-pressed={isFixed}
          className={classnames(
            'relative z-10 flex flex-1 h-full items-center justify-center gap-2 px-6 rounded-button',
            'text-[17px] font-medium tracking-[-0.34px] leading-[22px] whitespace-nowrap',
            'transition-colors duration-200',
            isFixed ? 'text-on-primary' : 'text-on-surface-variant',
            !isFixed && disabled && 'opacity-[0.38]',
          )}
        >
          <span>Fixed price</span>
          <RadioIcon selected={isFixed} />
        </button>

        {/* Taxameter option */}
        <button
          type="button"
          onClick={() => handleSelect(false)}
          disabled={disabled}
          aria-pressed={!isFixed}
          className={classnames(
            'relative z-10 flex flex-1 h-full items-center justify-center gap-2 px-6 rounded-button',
            'text-[17px] font-medium tracking-[-0.34px] leading-[22px] whitespace-nowrap',
            'transition-colors duration-200',
            !isFixed ? 'text-on-primary' : 'text-on-surface-variant',
            isFixed && disabled && 'opacity-[0.38]',
          )}
        >
          <span>Taxameter</span>
          <RadioIcon selected={!isFixed} />
        </button>
      </div>

      {/* Error/info messages from parent (SelectHow passes these as children) */}
      {children}
    </div>
  )
}
